import {
  Activity,
  Clock,
  Heart,
  LucideIcon,
  Music,
  Volume2,
  Waves,
  Wind,
  Eye,
  FileText,
} from 'lucide-react';
import { ContentType } from '@/types/custom';

// constants from environment variables
export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
export const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || '';

/**
 * Where our own images, recordings and transcripts are served from.
 *
 * Empty means Supabase Storage, which is the arrangement today. Set it to the
 * R2 domain (no trailing slash) to serve the mirror instead -- see assetUrl()
 * in src/lib/utils.ts and scripts/mirror-to-r2.py.
 */
export const assetBaseUrl = (import.meta.env.VITE_ASSET_BASE_URL || '').replace(/\/$/, '');

// Which app variant this build serves. Both variants share the same source
// tree and Supabase DB; VITE_APP_VARIANT swaps the outward-facing identity
// (bundle ID, web domain, login flow, gated content).
export type AppVariant = 'hartzorg' | 'hoofdnaarhart';

const rawVariant = (import.meta.env.VITE_APP_VARIANT ?? 'hartzorg') as string;
if (rawVariant !== 'hartzorg' && rawVariant !== 'hoofdnaarhart') {
  console.error(
    `Unknown VITE_APP_VARIANT '${rawVariant}', falling back to 'hartzorg'`,
  );
}
export const APP_VARIANT: AppVariant =
  rawVariant === 'hoofdnaarhart' ? 'hoofdnaarhart' : 'hartzorg';
export const IS_HARTZORG = APP_VARIANT === 'hartzorg';
export const IS_HOOFDNAARHART = APP_VARIANT === 'hoofdnaarhart';

// Web domain served by this variant's build. Used as the Supabase auth
// redirectTo base in Capacitor builds so email links open the installed app
// via Universal Links / App Links instead of resolving to capacitor://localhost.
export const PUBLIC_APP_ORIGIN = IS_HOOFDNAARHART
  ? 'https://hoofdnaarhart.app'
  : 'https://hartzorg.app';

// Host the DeepLinkListener accepts as "ours" (rejects appUrlOpen events for
// other domains).
export const APP_LINK_HOST = IS_HOOFDNAARHART ? 'hoofdnaarhart.app' : 'hartzorg.app';

// Where the HoofdnaarHart app lives on the web. The Hartzorg site used to host
// /hoofdhartlogin, so links to it are still out there and need somewhere to go.
export const HOOFDNAARHART_ORIGIN = 'https://hoofdnaarhart.app';

// Login route for this variant. Redirects from ProtectedLayout, signOut, and
// account-deletion flow all resolve here.
export const LOGIN_PATH = IS_HOOFDNAARHART ? '/hoofdhartlogin' : '/login';

export const isCapacitor = () => !!(window as any).Capacitor;

// Build an absolute HTTPS URL for a Supabase auth redirectTo. In the browser
// this uses the current origin; in a Capacitor native build it always uses
// PUBLIC_APP_ORIGIN so the OS can hand the URL to the installed app.
export const authRedirectUrl = (path: string) => {
  const base = isCapacitor() ? PUBLIC_APP_ORIGIN : window.location.origin;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Binaural Beats are admin-only for now, the same treatment the 7-Daagse
 * Challenge gets in the sidebar: the section holds a handful of tracks, and a
 * near-empty section is what an App Store reviewer reads as an app with too
 * little in it. Admins still see and manage everything, so the library can be
 * filled out in place.
 *
 * Flip this to true to release it -- the nav entry, the search and favourites
 * tabs, the pages and the content itself all key off this one value.
 */
/** Where the bijeenkomsten, both programmes and the sign-up live. */
export const MARKETING_URL = 'https://hoofdnaarhart.nl';

/**
 * The documents a login screen offers, by name.
 *
 * Only what someone agrees to by registering belongs here. Licenties is the
 * counter-example: a 400-entry attribution list is required reading for nobody
 * at the login screen, and it lives under Profiel > Documenten instead. Both
 * login pages read this list, so they cannot drift apart -- they already had,
 * which is how Licenties ended up on one of the two.
 */
export const LOGIN_LEGAL_DOCUMENTS = ['Algemene Voorwaarden', 'Privacy Beleid'];

export const BINAURAL_BEATS_RELEASED = false;

export const canSeeBinauralBeats = (isAdmin: boolean) =>
  BINAURAL_BEATS_RELEASED || isAdmin;

/**
 * Lookup links for respective pages.
 * So we can consistently define and also generate them.
 */
export const ROUTES = {
  NOT_FOUND: '/niet-gevonden',
  MEDITATIONS: '/meditaties',
  MUSIC: '/muziek',
  BINAURAL_BEATS: '/binaural-beats',
  //   LOGIN: "/login",
  //   RESET_PASSWORD: "/reset-password",
  //   ADMIN_LOGIN: "/admin/login",
  //   ADMIN_RESET_PASSWORD_CONFIRM: "/admin/reset-password-confirm",
  //   HOME: "/",
  //   MEDITATIONS: "/meditaties",
  //   FAVORITES: "/favorieten",
  NATURE_SOUNDS: '/natuurgeluiden',
  //   PROFILE: "/profiel",
  //   TEAM: "/team",
  //   BINAURAL_BEATS: "/binaural-beats",
  //   INSPIRATION_CARDS: "/inspiratiekaarten",
  TIPS: '/tips',
  EXERCISES: '/oefeningen',
  CHALLENGE: '/7-daagse-challenge',
  //   GENERAL_TIPS: "/algemene-tips",
  //   SEARCH: "/search",
} as const;

export const getDeepLink = (contentType: ContentType, id: string): string => {
  if (contentType === 'meditation' || contentType === 'journey' || contentType === 'minutjes') {
    return `${ROUTES.MEDITATIONS}/${id}`;
  }
  if (contentType === 'binaural') {
    return `${ROUTES.BINAURAL_BEATS}/${id}`;
  }
  if (contentType === 'music') {
    return `${ROUTES.MUSIC}?actief=${id}`;
  }
  if (contentType === 'nature_sound') {
    return `${ROUTES.NATURE_SOUNDS}?actief=${id}`;
  }
  if (contentType === 'breathing' || contentType === 'body_exercise' || contentType === 'dance_break') {
    return `${ROUTES.EXERCISES}?actief=${id}`;
  }
  if (contentType === 'article') {
    return `${ROUTES.TIPS}?actief=${id}`;
  }
  console.error(`unhandled content type: '${contentType}'`);
  // if (contentType === 'music') {
  // return `/${contentType}/${id}`;
  return '';
};

// map content type to short description (for UI)
export const CONTENT_TYPES: Record<ContentType, string> = {
  minutjes: 'Minu-tje',
  meditation: 'Korte Meditatie',
  journey: 'Visualisatie Reis',
  nature_sound: 'Natuurgeluid',
  music: 'Muziek',
  binaural: 'Binaural Beat',
  breathing: 'Ademhalingsoefeningen',
  body_exercise: 'Lichaamsbewustzijnsoefeningen',
  dance_break: 'Dance Breaks',
  article: 'Artikel',
};

// TODO: possibly define better icon set
export const IconMap: Record<ContentType, LucideIcon> = {
  minutjes: Clock,
  meditation: Heart,
  journey: Eye,
  binaural: Volume2,
  music: Music,
  nature_sound: Waves,
  breathing: Wind,
  body_exercise: Activity,
  dance_break: Music,
  article: FileText,
} as const;

// color mapping for content types (in graphs)
export const CONTENT_TYPE_COLORS: Record<ContentType, string> = {
  meditation: '#4285F4',
  journey: '#34A853',
  music: '#4FC3F7',
  binaural: '#EA4335',
  nature_sound: '#9C27B0',
  minutjes: '#FFC107',
  breathing: '#FF6B6B',
  body_exercise: '#26A69A',
  dance_break: '#AB47BC',
  article: '#FFD700',
};
